import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { 
  Calendar, Clock, Users, LogOut, Search, Bell, 
  CheckCircle, XCircle, Play, RefreshCw, Home,
  Phone, Video, MapPin, AlertCircle, Send,
  UserCheck, Timer, Activity, MessageSquare,
  Stethoscope, ClipboardList, ArrowRight, Eye,
  User, Mail, CreditCard, FileText, ChevronRight,
  CalendarDays, UserPlus, History, Settings,
  Menu, X, Plus, Filter, MoreVertical, Heart,
  Sparkles, TrendingUp, Zap, Star, Gift, Loader2, Edit,
  Award, DollarSign, Trash2, BarChart3, QrCode, Camera
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { ClinicLogo } from '@/hooks/useClinicBranding';
import { usePermissions } from '@/context/PermissionContext';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import MemberDetailDialog from '@/components/admin/Members/MemberDetailDialog';
import MemberServiceUsageDialog from '@/components/admin/Members/MemberServiceUsageDialog';
import MedicalNotesPanel from '@/components/MedicalNotesPanel';
import { Scanner } from '@yudiel/react-qr-scanner';

// Status configuration
const STATUS_CONFIG = {
  scheduled: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Scheduled', gradient: 'from-blue-500 to-blue-600' },
  confirmed: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Confirmed', gradient: 'from-emerald-500 to-emerald-600' },
  checked_in: { color: 'bg-teal-100 text-teal-700', icon: UserCheck, label: 'Checked In', gradient: 'from-teal-500 to-teal-600' },
  in_progress: { color: 'bg-violet-100 text-violet-700', icon: Play, label: 'In Progress', gradient: 'from-violet-500 to-violet-600' },
  completed: { color: 'bg-slate-100 text-slate-600', icon: CheckCircle, label: 'Completed', gradient: 'from-slate-400 to-slate-500' },
  cancelled: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Cancelled', gradient: 'from-red-500 to-red-600' },
  no_show: { color: 'bg-orange-100 text-orange-700', icon: AlertCircle, label: 'No Show', gradient: 'from-orange-500 to-orange-600' }
};

const MEMBERSHIP_STATUS = {
  active: { color: 'bg-emerald-100 text-emerald-700', label: 'Active' },
  expired: { color: 'bg-red-100 text-red-700', label: 'Expired' },
  pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
  cancelled: { color: 'bg-slate-100 text-slate-600', label: 'Cancelled' }
};

const StaffDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canView, canCreate, canEdit, roleName, loading: permissionsLoading } = usePermissions();
  const { isDark, fonts, mode } = useTheme();
  
  // Navigation state
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Data states
  const [appointments, setAppointments] = useState([]);
  const [members, setMembers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [clinicBranding, setClinicBranding] = useState(null);
  const [stats, setStats] = useState({});
  const [messages, setMessages] = useState([]);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const [messageText, setMessageText] = useState('');
  const [appointmentFilter, setAppointmentFilter] = useState('today');
  const [selectedConversation, setSelectedConversation] = useState(null);
  
  // Create/Edit forms
  const [showCreateAppointment, setShowCreateAppointment] = useState(false);
  const [showEditMember, setShowEditMember] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState({
    member_id: '',
    appointment_date: '',
    appointment_time: '',
    appointment_type: 'consultation',
    reason: '',
    duration_minutes: 30
  });
  const [editMemberForm, setEditMemberForm] = useState({});
  
  // Member management states (same as Admin)
  const [plans, setPlans] = useState([]);
  const [memberDetailDialogOpen, setMemberDetailDialogOpen] = useState(false);
  const [serviceUsageDialogOpen, setServiceUsageDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [assignPlanDialogOpen, setAssignPlanDialogOpen] = useState(false);
  const [selectedMemberForAction, setSelectedMemberForAction] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  // Additional payment states (matching Admin dashboard)
  const [startDate, setStartDate] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Create new member states
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [creatingMember, setCreatingMember] = useState(false);
  const [newMemberData, setNewMemberData] = useState({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    emergency_contact: '',
    emergency_phone: '',
    plan_id: '',
    send_welcome_email: true
  });

  // Fast Check-in states
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkInMember, setCheckInMember] = useState(null);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkInReason, setCheckInReason] = useState('');
  const [recentCheckIns, setRecentCheckIns] = useState([]);

  // QR Scanner states
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [qrScanning, setQrScanning] = useState(true);
  const [scannedMember, setScannedMember] = useState(null);
  const [scanError, setScanError] = useState(null);
  
  // Patient Impersonation State
  const [impersonating, setImpersonating] = useState(false);

  // Handle View as Patient - Impersonate patient to help them
  const handleViewAsPatient = async (member) => {
    if (impersonating) return;
    
    setImpersonating(true);
    try {
      const response = await axios.post(`${API}/clinic/impersonate-patient`, {
        member_id: member.id,
        reason: 'Staff assisting patient'
      });
      
      if (response.data.success) {
        // Store impersonation data
        localStorage.setItem('patient_impersonation_data', JSON.stringify({
          type: 'patient_impersonation',
          impersonated_patient: response.data.impersonated_patient,
          original_staff_id: response.data.original_staff_id,
          session_id: response.data.session_id,
          expires_at: response.data.expires_at,
          return_url: response.data.return_url
        }));
        
        // Store original user and token for restoration
        localStorage.setItem('original_admin_token', localStorage.getItem('token'));
        localStorage.setItem('original_admin_user', localStorage.getItem('user'));
        
        // Update token and user to impersonated patient
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify({
          id: response.data.impersonated_patient.id,
          email: response.data.impersonated_patient.email,
          full_name: response.data.impersonated_patient.full_name,
          role: response.data.impersonated_patient.role,
          clinic_id: response.data.impersonated_patient.clinic_id
        }));
        
        toast.success(response.data.message);
        
        // Redirect to patient dashboard
        window.location.href = '/patient';
      }
    } catch (error) {
      console.error('Error starting patient view:', error);
      const errorMessage = error.response?.data?.detail || 'Unable to view as patient';
      toast.error(errorMessage);
    } finally {
      setImpersonating(false);
    }
  };

  // Update time
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [appointmentsRes, membersRes, brandingRes, conversationsRes, plansRes, gatewayRes] = await Promise.all([
        canView('appointments') ? axios.get(`${API}/appointments`) : Promise.resolve({ data: [] }),
        canView('patients') ? axios.get(`${API}/members`) : Promise.resolve({ data: [] }),
        axios.get(`${API}/clinic/branding`).catch(() => ({ data: null })),
        axios.get(`${API}/conversations`).catch(() => ({ data: [] })),
        axios.get(`${API}/plans`).catch(() => ({ data: [] })),
        axios.get(`${API}/clinic-payments/gateway-status`).catch(() => ({ data: null }))
      ]);

      const allAppointments = appointmentsRes.data || [];
      const allMembers = membersRes.data || [];
      
      // Calculate stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const todayApts = allAppointments.filter(apt => {
        const aptDate = new Date(apt.appointment_date);
        return aptDate >= today && aptDate < tomorrow;
      });

      setAppointments(allAppointments);
      setMembers(allMembers);
      setClinicBranding(brandingRes.data);
      setConversations(conversationsRes.data || []);
      setPlans(plansRes.data || []);
      setGatewayStatus(gatewayRes.data);
      
      // Extract completed consultations
      const completedConsultations = allAppointments.filter(apt => apt.status === 'completed');
      setConsultations(completedConsultations);
      
      setStats({
        totalToday: todayApts.length,
        pending: todayApts.filter(a => a.status === 'scheduled').length,
        confirmed: todayApts.filter(a => a.status === 'confirmed').length,
        checkedIn: todayApts.filter(a => a.status === 'checked_in').length,
        inProgress: todayApts.filter(a => a.status === 'in_progress').length,
        completed: todayApts.filter(a => a.status === 'completed').length,
        totalMembers: allMembers.length,
        activeMembers: allMembers.filter(m => m.membership_status === 'active').length,
        unreadMessages: (conversationsRes.data || []).reduce((sum, c) => sum + (c.unread_count || 0), 0)
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Appointment actions
  const handleAppointmentAction = async (appointmentId, action) => {
    setActionLoading(prev => ({ ...prev, [appointmentId]: action }));
    try {
      const endpoints = {
        confirm: `${API}/appointments/${appointmentId}/confirm`,
        checkin: `${API}/appointments/${appointmentId}/check-in`,
        start: `${API}/appointments/${appointmentId}/start`,
        complete: `${API}/appointments/${appointmentId}/complete`,
        cancel: `${API}/appointments/${appointmentId}/cancel`,
        noshow: `${API}/appointments/${appointmentId}/no-show`
      };
      
      const messages = {
        confirm: 'Appointment confirmed',
        checkin: 'Patient checked in',
        start: 'Consultation started',
        complete: 'Appointment completed',
        cancel: 'Appointment cancelled',
        noshow: 'Marked as no-show'
      };

      if (action === 'cancel' && !window.confirm('Cancel this appointment?')) {
        setActionLoading(prev => ({ ...prev, [appointmentId]: null }));
        return;
      }

      await axios.post(endpoints[action], action === 'cancel' ? { reason: 'Cancelled by staff' } : {});
      toast.success(messages[action]);
      fetchData();
      setShowModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} appointment`);
    } finally {
      setActionLoading(prev => ({ ...prev, [appointmentId]: null }));
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;
    try {
      setActionLoading(prev => ({ ...prev, sendMessage: true }));
      await axios.post(`${API}/conversations/${selectedConversation.id}/messages`, {
        message: messageText,
        is_urgent: false
      });
      toast.success('Message sent');
      setMessageText('');
      // Refresh messages
      const res = await axios.get(`${API}/conversations/${selectedConversation.id}/messages`);
      setMessages(res.data || []);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setActionLoading(prev => ({ ...prev, sendMessage: false }));
    }
  };

  // Start conversation with member
  const handleStartConversation = async (member) => {
    try {
      setActionLoading(prev => ({ ...prev, startConv: true }));
      const res = await axios.post(`${API}/conversations`, {
        member_id: member.id,
        message: 'Hello! How can we help you today?'
      });
      setSelectedConversation(res.data);
      setActiveSection('messages');
      fetchData();
      toast.success('Conversation started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    } finally {
      setActionLoading(prev => ({ ...prev, startConv: false }));
    }
  };

  // Load conversation messages
  const loadConversationMessages = async (conversation) => {
    setSelectedConversation(conversation);
    try {
      const res = await axios.get(`${API}/conversations/${conversation.id}/messages`);
      setMessages(res.data || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
      setMessages([]);
    }
  };

  // Create appointment
  const handleCreateAppointment = async () => {
    if (!appointmentForm.member_id || !appointmentForm.appointment_date || !appointmentForm.appointment_time) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      setActionLoading(prev => ({ ...prev, createAppointment: true }));
      const dateTime = new Date(`${appointmentForm.appointment_date}T${appointmentForm.appointment_time}`);
      await axios.post(`${API}/appointments`, {
        member_id: appointmentForm.member_id,
        appointment_date: dateTime.toISOString(),
        appointment_type: appointmentForm.appointment_type,
        reason: appointmentForm.reason,
        duration_minutes: parseInt(appointmentForm.duration_minutes) || 30,
        appointment_mode: 'in_person'
      });
      toast.success('Appointment created successfully');
      setShowCreateAppointment(false);
      setAppointmentForm({
        member_id: '',
        appointment_date: '',
        appointment_time: '',
        appointment_type: 'consultation',
        reason: '',
        duration_minutes: 30
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    } finally {
      setActionLoading(prev => ({ ...prev, createAppointment: false }));
    }
  };

  // Edit member
  const handleEditMember = async () => {
    if (!editMemberForm.id) return;
    try {
      setActionLoading(prev => ({ ...prev, editMember: true }));
      await axios.put(`${API}/members/${editMemberForm.id}`, {
        full_name: editMemberForm.full_name,
        email: editMemberForm.email,
        phone: editMemberForm.phone,
        date_of_birth: editMemberForm.date_of_birth,
        address: editMemberForm.address
      });
      toast.success('Member updated successfully');
      setShowEditMember(false);
      setEditMemberForm({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    } finally {
      setActionLoading(prev => ({ ...prev, editMember: false }));
    }
  };

  // Open edit member dialog
  const openEditMember = (member) => {
    setEditMemberForm({
      id: member.id,
      full_name: member.full_name || '',
      email: member.email || '',
      phone: member.phone || '',
      date_of_birth: member.date_of_birth || '',
      address: member.address || ''
    });
    setShowEditMember(true);
  };

  // ============ Member Management Functions (Same as Admin) ============
  
  // Open member detail dialog
  const openMemberDetail = (member) => {
    setSelectedMemberForAction(member);
    setMemberDetailDialogOpen(true);
  };

  // Open service usage dialog
  const openServiceUsage = (member) => {
    setSelectedMemberForAction(member);
    setServiceUsageDialogOpen(true);
  };

  // Open notes dialog
  const openNotesDialog = (member) => {
    setSelectedMemberForAction(member);
    setNotesDialogOpen(true);
  };

  // Open assign plan dialog
  const openAssignPlanDialog = (member) => {
    setSelectedMemberForAction(member);
    setSelectedPlan(member.membership_plan_id || '');
    setPaymentMethod('cash');
    setPaymentNotes('');
    setStartDate('');
    setReferenceNumber('');
    setPreview(null);
    setShowPreview(false);
    setAssignPlanDialogOpen(true);
  };

  // Get payment preview (matching Admin dashboard)
  const handleGetPreview = async () => {
    if (!selectedMemberForAction || !selectedPlan) return;
    
    setLoadingPreview(true);
    try {
      const response = await axios.post(`${API}/members/${selectedMemberForAction.id}/membership-preview`, {
        plan_id: selectedPlan,
        start_date: startDate || null
      });
      setPreview(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate payment');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Assign/Change membership plan (matching Admin dashboard)
  const handleAssignPlan = async () => {
    if (!selectedMemberForAction || !selectedPlan) return;
    
    // If using gateway payment, redirect to payment page
    if (paymentMethod === 'gateway') {
      setAssignLoading(true);
      try {
        const response = await axios.post(`${API}/members/${selectedMemberForAction.id}/assign-membership`, {
          plan_id: selectedPlan,
          start_date: startDate || null,
          payment_method: 'gateway',
          origin_url: window.location.origin
        });
        
        if (response.data.checkout_url) {
          toast.info('Redirecting to payment page...');
          window.location.href = response.data.checkout_url;
        } else {
          toast.success('Membership plan assigned successfully');
          setAssignPlanDialogOpen(false);
          fetchData();
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      } finally {
        setAssignLoading(false);
      }
      return;
    }
    
    // Manual payment methods
    setAssignLoading(true);
    try {
      await axios.post(`${API}/members/${selectedMemberForAction.id}/assign-membership`, {
        plan_id: selectedPlan,
        start_date: startDate || null,
        payment_method: paymentMethod,
        payment_notes: paymentNotes,
        reference_number: referenceNumber || null
      });
      toast.success('Membership plan assigned successfully');
      setAssignPlanDialogOpen(false);
      setShowPreview(false);
      setPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign plan');
    } finally {
      setAssignLoading(false);
    }
  };

  // Renew membership
  const handleRenewMembership = async (memberId) => {
    setActionLoading(prev => ({ ...prev, [`renew_${memberId}`]: true }));
    try {
      await axios.post(`${API}/members/${memberId}/renew`);
      toast.success('Membership renewed successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to renew membership');
    } finally {
      setActionLoading(prev => ({ ...prev, [`renew_${memberId}`]: false }));
    }
  };

  // Activate membership manually
  const handleActivateMembership = async (member) => {
    setActionLoading(prev => ({ ...prev, [`activate_${member.id}`]: true }));
    try {
      await axios.post(`${API}/members/${member.id}/activate`);
      toast.success('Membership activated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate membership');
    } finally {
      setActionLoading(prev => ({ ...prev, [`activate_${member.id}`]: false }));
    }
  };

  // Delete member (soft delete - cancel membership)
  const handleCancelMembership = async (member) => {
    if (!window.confirm(`Are you sure you want to cancel ${member.full_name}'s membership?`)) return;
    
    setActionLoading(prev => ({ ...prev, [`cancel_${member.id}`]: true }));
    try {
      await axios.delete(`${API}/members/${member.id}`);
      toast.success('Membership cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel membership');
    } finally {
      setActionLoading(prev => ({ ...prev, [`cancel_${member.id}`]: false }));
    }
  };

  // Get payment history for a member
  const [paymentHistoryDialogOpen, setPaymentHistoryDialogOpen] = useState(false);
  const [memberPayments, setMemberPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const openPaymentHistory = async (member) => {
    setSelectedMemberForAction(member);
    setPaymentHistoryDialogOpen(true);
    setLoadingPayments(true);
    try {
      const response = await axios.get(`${API}/payments/member/${member.id}`);
      setMemberPayments(response.data || []);
    } catch (error) {
      toast.error('Failed to load payment history');
      setMemberPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  // ============ End Member Management Functions ============

  // Create new member manually (Walk-in registration)
  const handleCreateMember = async () => {
    if (!newMemberData.full_name || !newMemberData.email) {
      toast.error('Please fill in required fields (Name and Email)');
      return;
    }

    setCreatingMember(true);
    try {
      const response = await axios.post(`${API}/members/manual`, {
        full_name: newMemberData.full_name,
        email: newMemberData.email,
        phone: newMemberData.phone,
        date_of_birth: newMemberData.date_of_birth,
        address: newMemberData.address,
        emergency_contact: newMemberData.emergency_contact,
        emergency_phone: newMemberData.emergency_phone,
        plan_id: newMemberData.plan_id || null,
        send_welcome_email: newMemberData.send_welcome_email
      });

      toast.success(`Member ${response.data.patient_id} created successfully!`);
      setAddMemberDialogOpen(false);
      setNewMemberData({
        full_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        address: '',
        emergency_contact: '',
        emergency_phone: '',
        plan_id: '',
        send_welcome_email: true
      });
      fetchData();
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Member limit reached. Please contact administrator to upgrade the plan.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create member');
      }
    } finally {
      setCreatingMember(false);
    }
  };

  // ============ Fast Check-in Functions ============
  
  // Open fast check-in dialog
  const openFastCheckIn = (member) => {
    setCheckInMember(member);
    setCheckInReason('');
    setCheckInDialogOpen(true);
  };

  // Perform fast check-in
  const handleFastCheckIn = async () => {
    if (!checkInMember) return;
    
    setCheckInLoading(true);
    try {
      // Create a quick appointment with status "checked_in"
      const today = new Date();
      const appointmentData = {
        member_id: checkInMember.id,
        appointment_date: today.toISOString().split('T')[0],
        appointment_time: today.toTimeString().slice(0, 5),
        appointment_type: 'walk_in',
        status: 'checked_in',
        reason: checkInReason || 'Walk-in visit',
        duration_minutes: 30
      };

      await axios.post(`${API}/appointments`, appointmentData);
      
      toast.success(`${checkInMember.full_name} checked in successfully!`, {
        description: `Patient ID: ${checkInMember.patient_id}`
      });
      
      // Add to recent check-ins
      setRecentCheckIns(prev => [{
        member: checkInMember,
        time: new Date(),
        reason: checkInReason || 'Walk-in visit'
      }, ...prev].slice(0, 5));
      
      setCheckInDialogOpen(false);
      setCheckInMember(null);
      setCheckInReason('');
      fetchData(); // Refresh appointments
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in member');
    } finally {
      setCheckInLoading(false);
    }
  };

  // Quick check-in (one-click for active members)
  const handleQuickCheckIn = async (member) => {
    if (member.membership_status !== 'active') {
      toast.error('Only active members can be checked in');
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [`checkin_${member.id}`]: true }));
    try {
      const today = new Date();
      await axios.post(`${API}/appointments`, {
        member_id: member.id,
        appointment_date: today.toISOString().split('T')[0],
        appointment_time: today.toTimeString().slice(0, 5),
        appointment_type: 'walk_in',
        status: 'checked_in',
        reason: 'Quick check-in',
        duration_minutes: 30
      });
      
      toast.success(`${member.full_name} checked in!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setActionLoading(prev => ({ ...prev, [`checkin_${member.id}`]: false }));
    }
  };

  // ============ End Fast Check-in Functions ============

  // ============ QR Scanner Functions ============
  
  // Handle QR code scan result
  const handleQrScan = async (result) => {
    if (!result || !result[0]?.rawValue) return;
    
    const scannedData = result[0].rawValue;
    setQrScanning(false);
    setScanError(null);
    
    try {
      // Extract patient_id from the verification URL
      // URL format: https://domain.com/verify/{patient_id}?t={token}
      let patientId = null;
      
      // Try to parse as URL
      try {
        const url = new URL(scannedData);
        const pathParts = url.pathname.split('/');
        const verifyIndex = pathParts.indexOf('verify');
        if (verifyIndex !== -1 && pathParts[verifyIndex + 1]) {
          patientId = pathParts[verifyIndex + 1];
        }
      } catch {
        // If not a URL, check if it's a direct patient_id
        if (scannedData.startsWith('MED-') || scannedData.match(/^[A-Z]{3}-\d+$/)) {
          patientId = scannedData;
        }
      }
      
      if (!patientId) {
        setScanError('Invalid QR code format. Please scan a valid member card.');
        setQrScanning(true);
        return;
      }
      
      // Find member by patient_id
      const member = members.find(m => m.patient_id === patientId);
      
      if (!member) {
        // Try to fetch from API
        try {
          const response = await axios.get(`${API}/members/verify/${patientId}`);
          if (response.data) {
            setScannedMember({
              ...response.data,
              fromApi: true
            });
          } else {
            setScanError(`Member ${patientId} not found in this clinic.`);
            setQrScanning(true);
          }
        } catch {
          setScanError(`Member ${patientId} not found.`);
          setQrScanning(true);
        }
      } else {
        setScannedMember(member);
      }
    } catch (error) {
      console.error('QR scan error:', error);
      setScanError('Error processing QR code. Please try again.');
      setQrScanning(true);
    }
  };

  // Handle QR check-in confirmation
  const handleQrCheckIn = async () => {
    if (!scannedMember) return;
    
    if (scannedMember.membership_status !== 'active') {
      toast.error('This member does not have an active membership');
      return;
    }
    
    setCheckInLoading(true);
    try {
      const today = new Date();
      await axios.post(`${API}/appointments`, {
        member_id: scannedMember.id,
        appointment_date: today.toISOString().split('T')[0],
        appointment_time: today.toTimeString().slice(0, 5),
        appointment_type: 'walk_in',
        status: 'checked_in',
        reason: 'QR Check-in',
        duration_minutes: 30
      });
      
      toast.success(`${scannedMember.full_name} checked in successfully!`, {
        description: `Patient ID: ${scannedMember.patient_id}`
      });
      
      // Add to recent check-ins
      setRecentCheckIns(prev => [{
        member: scannedMember,
        time: new Date(),
        reason: 'QR Check-in'
      }, ...prev].slice(0, 5));
      
      // Reset and close
      setQrScannerOpen(false);
      setScannedMember(null);
      setQrScanning(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setCheckInLoading(false);
    }
  };

  // Reset QR scanner
  const resetQrScanner = () => {
    setScannedMember(null);
    setScanError(null);
    setQrScanning(true);
  };

  // ============ End QR Scanner Functions ============

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  // Helpers
  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '??';
  const formatTime = (dateStr) => new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  
  const getMemberInfo = (memberId) => members.find(m => m.id === memberId) || { full_name: 'Unknown Patient' };

  const getFilteredAppointments = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    let filtered = appointments;
    
    if (appointmentFilter === 'today') {
      filtered = appointments.filter(apt => {
        const d = new Date(apt.appointment_date);
        return d >= today && d < tomorrow;
      });
    } else if (appointmentFilter === 'upcoming') {
      filtered = appointments.filter(apt => {
        const d = new Date(apt.appointment_date);
        return d >= tomorrow && d < nextWeek;
      });
    } else if (appointmentFilter === 'pending') {
      filtered = appointments.filter(apt => apt.status === 'scheduled');
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(apt => {
        const member = getMemberInfo(apt.member_id);
        return member.full_name?.toLowerCase().includes(term) ||
               member.email?.toLowerCase().includes(term) ||
               apt.reason?.toLowerCase().includes(term);
      });
    }

    return filtered.sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
  };

  const getFilteredMembers = () => {
    if (!searchTerm) return members;
    const term = searchTerm.toLowerCase();
    return members.filter(m => 
      m.full_name?.toLowerCase().includes(term) ||
      m.email?.toLowerCase().includes(term) ||
      m.phone?.includes(term) ||
      m.patient_id?.toLowerCase().includes(term)
    );
  };

  // Get branding colors with fallback
  const primaryColor = clinicBranding?.primary_color || '#14b8a6';
  const secondaryColor = clinicBranding?.secondary_color || '#0d9488';

  // Navigation items
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, badge: null },
    { id: 'appointments', label: 'Appointments', icon: CalendarDays, badge: stats.pending > 0 ? stats.pending : null },
    { id: 'members', label: 'Members', icon: Users, badge: null },
    { id: 'consultations', label: 'Consultations', icon: Stethoscope, badge: null },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: stats.unreadMessages > 0 ? stats.unreadMessages : null }
  ];

  // Loading
  if (loading || permissionsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-teal-200 rounded-full animate-pulse"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-teal-600 rounded-full animate-spin border-t-transparent"></div>
          </div>
          <p className="mt-4 text-slate-600 font-medium">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  // Render Dashboard Section
  const renderDashboard = () => {
    const todayAppointments = appointments.filter(apt => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const aptDate = new Date(apt.appointment_date);
      return aptDate >= today && aptDate < tomorrow;
    }).sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));

    const nextAppointment = todayAppointments.find(apt => 
      new Date(apt.appointment_date) > new Date() && 
      !['completed', 'cancelled', 'no_show'].includes(apt.status)
    );

    return (
      <div className="space-y-6">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
              Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}, {user?.full_name?.split(' ')[0]}! 
              <span className="ml-2">👋</span>
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="text-white border-0 shadow-lg" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, boxShadow: `0 10px 25px -5px ${primaryColor}30` }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-xs font-medium">Today</p>
                  <p className="text-3xl font-bold">{stats.totalToday}</p>
                </div>
                <Calendar className="h-8 w-8 text-white/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white border-0 shadow-lg shadow-amber-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-xs font-medium">Pending</p>
                  <p className="text-3xl font-bold">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0 shadow-lg shadow-emerald-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-xs font-medium">Confirmed</p>
                  <p className="text-3xl font-bold">{stats.confirmed}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white border-0 shadow-lg shadow-cyan-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-100 text-xs font-medium">Checked In</p>
                  <p className="text-3xl font-bold">{stats.checkedIn}</p>
                </div>
                <UserCheck className="h-8 w-8 text-cyan-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white border-0 shadow-lg shadow-violet-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-violet-100 text-xs font-medium">In Progress</p>
                  <p className="text-3xl font-bold">{stats.inProgress}</p>
                </div>
                <Play className="h-8 w-8 text-violet-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-slate-500 to-slate-600 text-white border-0 shadow-lg shadow-slate-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-xs font-medium">Completed</p>
                  <p className="text-3xl font-bold">{stats.completed}</p>
                </div>
                <Activity className="h-8 w-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Next Appointment Banner */}
        {nextAppointment && (
          <Card className="text-white border-0 overflow-hidden shadow-xl" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
            <CardContent className="p-0">
              <div className="flex flex-col md:flex-row items-center">
                <div className="flex-1 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-5 w-5 text-white/70" />
                    <span className="text-white/70 text-sm font-semibold uppercase tracking-wide">Next Patient</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <Avatar className="h-16 w-16 bg-white/20 border-2 border-white/30">
                      <AvatarFallback className="bg-transparent text-white text-xl font-bold">
                        {getInitials(getMemberInfo(nextAppointment.member_id).full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-2xl font-bold">{getMemberInfo(nextAppointment.member_id).full_name}</h3>
                      <p className="text-white/70 flex items-center gap-2 mt-1">
                        <Clock className="h-4 w-4" />
                        {formatTime(nextAppointment.appointment_date)} • {nextAppointment.appointment_type?.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-8 py-6 text-center md:border-l border-white/20">
                  <p className="text-white/70 text-xs uppercase tracking-wide mb-1">Starts In</p>
                  <p className="text-4xl font-bold">
                    {(() => {
                      const diff = Math.round((new Date(nextAppointment.appointment_date) - new Date()) / 60000);
                      if (diff < 0) return 'Now';
                      if (diff < 60) return `${diff}m`;
                      return `${Math.floor(diff / 60)}h ${diff % 60}m`;
                    })()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions & Today's Schedule */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Today's Appointments */}
          <div className="lg:col-span-2">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarDays className="h-5 w-5" style={{ color: primaryColor }} />
                    Today's Schedule
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveSection('appointments')}>
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  {todayAppointments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                      <Calendar className="h-12 w-12 mb-3 opacity-50" />
                      <p className="font-medium">No appointments today</p>
                      <p className="text-sm">Enjoy your free time!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {todayAppointments.slice(0, 8).map(apt => {
                        const member = getMemberInfo(apt.member_id);
                        const status = STATUS_CONFIG[apt.status] || STATUS_CONFIG.scheduled;
                        const StatusIcon = status.icon;
                        return (
                          <div 
                            key={apt.id}
                            onClick={() => { setSelectedItem(apt); setModalType('appointment'); setShowModal(true); }}
                            className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all group"
                          >
                            <Avatar className="h-12 w-12" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                              <AvatarFallback className="bg-transparent text-white font-semibold">
                                {getInitials(member.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-slate-900 dark:text-white truncate">{member.full_name}</h4>
                                <Badge className={`${status.color} text-xs`}>
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {status.label}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-500 truncate">
                                {formatTime(apt.appointment_date)} • {apt.appointment_type?.replace('_', ' ')}
                              </p>
                            </div>
                            <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats & Actions */}
          <div className="space-y-6">
            {/* Members Overview */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" style={{ color: primaryColor }} />
                  Members
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <Heart className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Active Members</p>
                        <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.activeMembers}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <Users className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Members</p>
                        <p className="text-xl font-bold text-slate-900 dark:text-white">{stats.totalMembers}</p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setActiveSection('members')}
                  >
                    View All Members
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Messages Preview */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" style={{ color: primaryColor }} />
                  Messages
                  {stats.unreadMessages > 0 && (
                    <Badge className="bg-red-500 text-white ml-2">{stats.unreadMessages}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {conversations.length === 0 ? (
                  <div className="text-center py-6 text-slate-400">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No messages yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {conversations.slice(0, 3).map((conv, i) => (
                      <div 
                        key={i}
                        onClick={() => setActiveSection('messages')}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-teal-100 text-teal-700 text-sm">
                            {getInitials(conv.participant_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{conv.participant_name}</p>
                          <p className="text-xs text-slate-500 truncate">{conv.last_message}</p>
                        </div>
                        {conv.unread_count > 0 && (
                          <Badge className="bg-teal-500 text-white text-xs">{conv.unread_count}</Badge>
                        )}
                      </div>
                    ))}
                    <Button 
                      variant="ghost" 
                      className="w-full text-teal-600"
                      onClick={() => setActiveSection('messages')}
                    >
                      View All Messages
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  // Render Appointments Section
  const renderAppointments = () => {
    const filteredAppointments = getFilteredAppointments();
    
    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Appointments</h1>
            <p className="text-slate-500">Manage your daily schedule</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search patients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            <Select value={appointmentFilter} onValueChange={setAppointmentFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="pending">Needs Action</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            {canCreate('appointments') && (
              <Button onClick={() => setShowCreateAppointment(true)} style={{ backgroundColor: primaryColor }}>
                <Plus className="h-4 w-4 mr-2" /> New Appointment
              </Button>
            )}
          </div>
        </div>

        {filteredAppointments.length === 0 ? (
          <Card className="p-12 text-center">
            <CalendarDays className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No appointments found</h3>
            <p className="text-slate-500 dark:text-slate-400">Try adjusting your filters</p>
            {canCreate('appointments') && (
              <Button onClick={() => setShowCreateAppointment(true)} className="mt-4" style={{ backgroundColor: primaryColor }}>
                <Plus className="h-4 w-4 mr-2" /> Create Appointment
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredAppointments.map(apt => {
              const member = getMemberInfo(apt.member_id);
              const status = STATUS_CONFIG[apt.status] || STATUS_CONFIG.scheduled;
              const StatusIcon = status.icon;
              const isLoading = actionLoading[apt.id];

              return (
                <Card 
                  key={apt.id} 
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => { setSelectedItem(apt); setModalType('appointment'); setShowModal(true); }}
                >
                  <div className={`h-1.5 bg-gradient-to-r ${status.gradient}`} />
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                          <AvatarFallback className="bg-transparent text-white font-semibold">
                            {getInitials(member.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="font-semibold text-slate-900 dark:text-white">{member.full_name}</h4>
                          <p className="text-sm text-slate-500">{member.email || member.phone}</p>
                        </div>
                      </div>
                      <Badge className={status.color}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {status.label}
                      </Badge>
                    </div>

                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span className="font-medium">{formatTime(apt.appointment_date)}</span>
                        <span className="text-slate-400">•</span>
                        <span>{formatDate(apt.appointment_date)}</span>
                      </div>
                      {apt.reason && (
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-400" />
                          <span className="truncate">{apt.reason}</span>
                        </div>
                      )}
                    </div>

                    {canEdit('appointments') && !['completed', 'cancelled', 'no_show'].includes(apt.status) && (
                      <div className="flex flex-wrap gap-2 pt-3 border-t" onClick={e => e.stopPropagation()}>
                        {apt.status === 'scheduled' && (
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleAppointmentAction(apt.id, 'confirm')}
                            disabled={isLoading}
                          >
                            {isLoading === 'confirm' ? '...' : <><CheckCircle className="h-4 w-4 mr-1" /> Confirm</>}
                          </Button>
                        )}
                        {apt.status === 'confirmed' && (
                          <Button 
                            size="sm" 
                            className="bg-cyan-600 hover:bg-cyan-700"
                            onClick={() => handleAppointmentAction(apt.id, 'checkin')}
                            disabled={isLoading}
                          >
                            {isLoading === 'checkin' ? '...' : <><UserCheck className="h-4 w-4 mr-1" /> Check In</>}
                          </Button>
                        )}
                        {apt.status === 'checked_in' && (
                          <Button 
                            size="sm" 
                            className="bg-violet-600 hover:bg-violet-700"
                            onClick={() => handleAppointmentAction(apt.id, 'start')}
                            disabled={isLoading}
                          >
                            {isLoading === 'start' ? '...' : <><Play className="h-4 w-4 mr-1" /> Start</>}
                          </Button>
                        )}
                        {apt.status === 'in_progress' && (
                          <Button 
                            size="sm" 
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => handleAppointmentAction(apt.id, 'complete')}
                            disabled={isLoading}
                          >
                            {isLoading === 'complete' ? '...' : <><CheckCircle className="h-4 w-4 mr-1" /> Complete</>}
                          </Button>
                        )}
                        {['scheduled', 'confirmed'].includes(apt.status) && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-orange-600 border-orange-200 hover:bg-orange-50"
                              onClick={() => handleAppointmentAction(apt.id, 'noshow')}
                              disabled={isLoading}
                            >
                              {isLoading === 'noshow' ? '...' : 'No Show'}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleAppointmentAction(apt.id, 'cancel')}
                              disabled={isLoading}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render Members Section - Full functionality like Admin Dashboard
  const renderMembers = () => {
    const filteredMembers = getFilteredMembers();
    
    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Members</h1>
            <p className="text-slate-500 dark:text-slate-400">{members.length} registered members • {stats.activeMembers || 0} active</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, phone, ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64 dark:bg-slate-800"
              />
            </div>
            <Button 
              variant="outline"
              onClick={() => {
                setQrScannerOpen(true);
                setScannedMember(null);
                setScanError(null);
                setQrScanning(true);
              }}
              className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-600 dark:text-purple-400"
              data-testid="qr-scanner-btn"
            >
              <QrCode className="h-4 w-4 mr-2" />
              Scan QR
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                const activeMembers = members.filter(m => m.membership_status === 'active');
                if (activeMembers.length > 0) {
                  setCheckInMember(null);
                  setCheckInDialogOpen(true);
                } else {
                  toast.error('No active members available for check-in');
                }
              }}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-600 dark:text-emerald-400"
              data-testid="fast-checkin-btn"
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Fast Check-in
            </Button>
            <Button 
              onClick={() => setAddMemberDialogOpen(true)}
              style={{ backgroundColor: primaryColor }}
              data-testid="add-member-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Member
            </Button>
          </div>
        </div>

        {filteredMembers.length === 0 ? (
          <Card className="p-12 text-center dark:bg-slate-800">
            <Users className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No members found</h3>
            <p className="text-slate-500 dark:text-slate-400">
              {searchTerm ? 'Try adjusting your search' : 'Click "Add Member" to register a walk-in patient'}
            </p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredMembers.map(member => {
              const membershipStatus = MEMBERSHIP_STATUS[member.membership_status] || MEMBERSHIP_STATUS.pending;
              const isExpiringSoon = member.membership_end_date && 
                new Date(member.membership_end_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
              
              return (
                <Card 
                  key={member.id}
                  className="overflow-hidden hover:shadow-lg transition-all dark:bg-slate-800 border-l-4"
                  style={{ borderLeftColor: member.membership_status === 'active' ? '#10b981' : '#94a3b8' }}
                >
                  <CardContent className="p-5">
                    {/* Member Header */}
                    <div className="flex items-start gap-4">
                      <Avatar className="h-14 w-14" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                        <AvatarFallback className="bg-transparent text-white text-lg font-bold">
                          {getInitials(member.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-slate-900 dark:text-white truncate">{member.full_name || 'Unknown'}</h4>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={membershipStatus.color}>{membershipStatus.label}</Badge>
                          {isExpiringSoon && member.membership_status === 'active' && (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">Expiring Soon</Badge>
                          )}
                        </div>
                        {member.patient_id && (
                          <p className="text-xs text-slate-400 mt-1">ID: {member.patient_id}</p>
                        )}
                      </div>
                    </div>
                    
                    <Separator className="my-4" />
                    
                    {/* Member Details */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                        <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{member.email || 'No email'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                        <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{member.phone || 'No phone'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                        <Award className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{member.plan_name || 'No plan assigned'}</span>
                      </div>
                      {member.membership_end_date && (
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                          <Calendar className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          <span>Expires: {new Date(member.membership_end_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Primary Action Buttons */}
                    <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t dark:border-slate-700">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openMemberDetail(member)}
                        className="hover:bg-teal-50 dark:hover:bg-teal-900/30"
                        data-testid={`view-detail-${member.patient_id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" /> Details
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openEditMember(member)}
                        className="hover:bg-blue-50 dark:hover:bg-blue-900/30"
                        data-testid={`edit-member-${member.patient_id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openServiceUsage(member)}
                        className="hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        data-testid={`service-usage-${member.patient_id}`}
                      >
                        <BarChart3 className="h-4 w-4 mr-1" /> Usage
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openPaymentHistory(member)}
                        className="hover:bg-green-50 dark:hover:bg-green-900/30"
                        data-testid={`payment-history-${member.patient_id}`}
                      >
                        <DollarSign className="h-4 w-4 mr-1" /> Payments
                      </Button>
                    </div>

                    {/* Secondary Action Buttons */}
                    <div className="grid grid-cols-5 gap-2 mt-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openNotesDialog(member)}
                        className="hover:bg-yellow-50 dark:hover:bg-yellow-900/30"
                        data-testid={`notes-${member.patient_id}`}
                        title="Medical Notes"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openAssignPlanDialog(member)}
                        className="hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                        data-testid={`assign-plan-${member.patient_id}`}
                        title="Assign/Change Plan"
                      >
                        <Award className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => handleStartConversation(member)}
                        className="hover:bg-blue-50 dark:hover:bg-blue-900/30"
                        disabled={actionLoading.startConv}
                        data-testid={`message-${member.patient_id}`}
                        title="Send Message"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewAsPatient(member)}
                        className="hover:bg-cyan-50 dark:hover:bg-cyan-900/30 text-cyan-600"
                        disabled={impersonating}
                        data-testid={`view-as-patient-${member.patient_id}`}
                        title="View as Patient"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {member.membership_status === 'active' && (
                        <Button 
                          size="sm"
                          onClick={() => handleQuickCheckIn(member)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={actionLoading[`checkin_${member.id}`]}
                          data-testid={`quick-checkin-${member.patient_id}`}
                          title="Quick Check-in"
                        >
                          {actionLoading[`checkin_${member.id}`] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Membership Actions */}
                    {member.membership_status === 'expired' && (
                      <div className="mt-3 pt-3 border-t dark:border-slate-700">
                        <Button 
                          size="sm" 
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleRenewMembership(member.id)}
                          disabled={actionLoading[`renew_${member.id}`]}
                        >
                          {actionLoading[`renew_${member.id}`] ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                          )}
                          Renew Membership
                        </Button>
                      </div>
                    )}
                    {member.membership_status === 'pending' && (
                      <div className="mt-3 pt-3 border-t dark:border-slate-700">
                        <Button 
                          size="sm" 
                          className="w-full bg-teal-600 hover:bg-teal-700"
                          onClick={() => handleActivateMembership(member)}
                          disabled={actionLoading[`activate_${member.id}`]}
                        >
                          {actionLoading[`activate_${member.id}`] ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          Activate Membership
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render Consultations Section
  const renderConsultations = () => {
    const completedAppointments = appointments.filter(apt => apt.status === 'completed')
      .sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date));
    
    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Consultations</h1>
            <p className="text-slate-500">History of completed appointments</p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search consultations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
        </div>

        {completedAppointments.length === 0 ? (
          <Card className="p-12 text-center dark:bg-slate-800">
            <Stethoscope className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No consultations yet</h3>
            <p className="text-slate-500 dark:text-slate-400">Completed appointments will appear here</p>
          </Card>
        ) : (
          <Card className="dark:bg-slate-800">
            <CardContent className="p-0">
              <div className="divide-y dark:divide-slate-700">
                {completedAppointments.map(apt => {
                  const member = getMemberInfo(apt.member_id);
                  return (
                    <div 
                      key={apt.id}
                      className="flex items-center gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                      onClick={() => { setSelectedItem(apt); setModalType('consultation'); setShowModal(true); }}
                    >
                      <Avatar className="h-12 w-12 bg-gradient-to-br from-slate-400 to-slate-500">
                        <AvatarFallback className="bg-transparent text-white font-semibold">
                          {getInitials(member.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-slate-900 dark:text-white">{member.full_name}</h4>
                          <Badge variant="secondary">{apt.appointment_type?.replace('_', ' ')}</Badge>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{apt.reason || 'General consultation'}</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="font-medium text-slate-900 dark:text-white">{formatDate(apt.appointment_date)}</p>
                        <p className="text-slate-500 dark:text-slate-400">{formatTime(apt.appointment_date)}</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-300" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Render Messages Section
  const renderMessages = () => {
    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Messages</h1>
            <p className="text-slate-500 dark:text-slate-400">Communicate with patients</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-250px)]">
          {/* Conversations List */}
          <Card className="lg:col-span-1 dark:bg-slate-800">
            <CardHeader className="pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search conversations..." className="pl-10" />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-380px)]">
                {conversations.length === 0 ? (
                  <div className="p-6 text-center text-slate-400">
                    <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No conversations</p>
                    <p className="text-xs mt-1">Start a conversation from the Members section</p>
                  </div>
                ) : (
                  <div className="divide-y dark:divide-slate-700">
                    {conversations.map((conv) => (
                      <div 
                        key={conv.id}
                        onClick={() => loadConversationMessages(conv)}
                        className={`flex items-center gap-3 p-4 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors ${selectedConversation?.id === conv.id ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
                        style={selectedConversation?.id === conv.id ? { borderLeft: `3px solid ${primaryColor}` } : {}}
                      >
                        <Avatar className="h-12 w-12" style={{ background: `linear-gradient(135deg, ${primaryColor}40, ${secondaryColor}40)` }}>
                          <AvatarFallback style={{ color: primaryColor }}>
                            {getInitials(conv.patient_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-slate-900 dark:text-white truncate">{conv.patient_name}</h4>
                            <span className="text-xs text-slate-400">
                              {conv.last_message_at ? new Date(conv.last_message_at).toLocaleDateString() : ''}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{conv.last_message}</p>
                        </div>
                        {(conv.unread_count_staff || 0) > 0 && (
                          <Badge style={{ backgroundColor: primaryColor }}>{conv.unread_count_staff}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Chat Area */}
          <Card className="lg:col-span-2 flex flex-col dark:bg-slate-800">
            {selectedConversation ? (
              <>
                <CardHeader className="border-b dark:border-slate-700 pb-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10" style={{ background: `linear-gradient(135deg, ${primaryColor}40, ${secondaryColor}40)` }}>
                      <AvatarFallback style={{ color: primaryColor }}>
                        {getInitials(selectedConversation.patient_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold dark:text-white">{selectedConversation.patient_name}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Patient</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 p-4 overflow-auto">
                  <ScrollArea className="h-[calc(100vh-450px)]">
                    {messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-slate-400 py-12">
                        <MessageSquare className="h-12 w-12 mb-3 opacity-50" />
                        <p>No messages yet</p>
                        <p className="text-sm">Start the conversation!</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((msg, i) => (
                          <div 
                            key={i} 
                            className={`flex ${msg.sender_type === 'staff' || msg.sender_type === 'clinic' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div 
                              className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                                msg.sender_type === 'staff' || msg.sender_type === 'clinic'
                                  ? 'text-white' 
                                  : 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white'
                              }`}
                              style={msg.sender_type === 'staff' || msg.sender_type === 'clinic' ? { backgroundColor: primaryColor } : {}}
                            >
                              <p>{msg.message}</p>
                              <p className={`text-xs mt-1 ${msg.sender_type === 'staff' || msg.sender_type === 'clinic' ? 'text-white/70' : 'text-slate-400'}`}>
                                {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
                <div className="border-t dark:border-slate-700 p-4">
                  <div className="flex gap-3">
                    <Textarea 
                      placeholder="Type your message..." 
                      className="resize-none dark:bg-slate-700 dark:border-slate-600"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      rows={2}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <Button 
                      style={{ backgroundColor: primaryColor }}
                      className="hover:opacity-90 px-6"
                      onClick={handleSendMessage}
                      disabled={actionLoading.sendMessage || !messageText.trim()}
                    >
                      {actionLoading.sendMessage ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400 dark:text-slate-500">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">Select a conversation</p>
                  <p className="text-sm">Choose from the list or start a new one from Members</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  };

  // Modal for details
  const renderModal = () => {
    if (!selectedItem) return null;

    if (modalType === 'appointment' || modalType === 'consultation') {
      const member = getMemberInfo(selectedItem.member_id);
      const status = STATUS_CONFIG[selectedItem.status] || STATUS_CONFIG.scheduled;
      const StatusIcon = status.icon;

      return (
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Stethoscope className="h-5 w-5" style={{ color: primaryColor }} />
                {modalType === 'consultation' ? 'Consultation Details' : 'Appointment Details'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <Avatar className="h-16 w-16" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                  <AvatarFallback className="bg-transparent text-white text-xl font-bold">
                    {getInitials(member.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold">{member.full_name}</h3>
                  <p className="text-sm text-slate-500">{member.email}</p>
                  {member.phone && <p className="text-sm text-slate-500">{member.phone}</p>}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Status</span>
                  <Badge className={status.color}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {status.label}
                  </Badge>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Date & Time</span>
                  <span className="font-medium">
                    {formatDate(selectedItem.appointment_date)} at {formatTime(selectedItem.appointment_date)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Type</span>
                  <span className="font-medium capitalize">{selectedItem.appointment_type?.replace('_', ' ')}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Duration</span>
                  <span className="font-medium">{selectedItem.duration_minutes || 30} minutes</span>
                </div>
                {selectedItem.reason && (
                  <>
                    <Separator />
                    <div>
                      <span className="text-slate-500 block mb-1">Reason</span>
                      <p className="font-medium">{selectedItem.reason}</p>
                    </div>
                  </>
                )}
              </div>

              {canEdit('appointments') && !['completed', 'cancelled', 'no_show'].includes(selectedItem.status) && (
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  {selectedItem.status === 'scheduled' && (
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                      onClick={() => handleAppointmentAction(selectedItem.id, 'confirm')}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" /> Confirm
                    </Button>
                  )}
                  {selectedItem.status === 'confirmed' && (
                    <Button 
                      className="bg-cyan-600 hover:bg-cyan-700 flex-1"
                      onClick={() => handleAppointmentAction(selectedItem.id, 'checkin')}
                    >
                      <UserCheck className="h-4 w-4 mr-2" /> Check In
                    </Button>
                  )}
                  {selectedItem.status === 'checked_in' && (
                    <Button 
                      className="bg-violet-600 hover:bg-violet-700 flex-1"
                      onClick={() => handleAppointmentAction(selectedItem.id, 'start')}
                    >
                      <Play className="h-4 w-4 mr-2" /> Start Consultation
                    </Button>
                  )}
                  {selectedItem.status === 'in_progress' && (
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 flex-1"
                      onClick={() => handleAppointmentAction(selectedItem.id, 'complete')}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" /> Complete
                    </Button>
                  )}
                  <Button 
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleAppointmentAction(selectedItem.id, 'cancel')}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    if (modalType === 'member') {
      const membershipStatus = MEMBERSHIP_STATUS[selectedItem.membership_status] || MEMBERSHIP_STATUS.pending;
      
      return (
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" style={{ color: primaryColor }} />
                Member Profile
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: `linear-gradient(135deg, ${primaryColor}15, ${secondaryColor}10)` }}>
                <Avatar className="h-20 w-20" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                  <AvatarFallback className="bg-transparent text-white text-2xl font-bold">
                    {getInitials(selectedItem.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">{selectedItem.full_name}</h3>
                  <Badge className={membershipStatus.color}>{membershipStatus.label}</Badge>
                  {selectedItem.patient_id && (
                    <p className="text-sm text-slate-500 mt-1">ID: {selectedItem.patient_id}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Mail className="h-4 w-4" />
                    <span className="text-xs">Email</span>
                  </div>
                  <p className="font-medium text-sm truncate">{selectedItem.email || 'N/A'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Phone className="h-4 w-4" />
                    <span className="text-xs">Phone</span>
                  </div>
                  <p className="font-medium text-sm">{selectedItem.phone || 'N/A'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <CreditCard className="h-4 w-4" />
                    <span className="text-xs">Plan</span>
                  </div>
                  <p className="font-medium text-sm">{selectedItem.plan_name || 'No plan'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-xs">Member Since</span>
                  </div>
                  <p className="font-medium text-sm">
                    {selectedItem.created_at ? new Date(selectedItem.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowModal(false);
                    setActiveSection('messages');
                  }}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send Message
                </Button>
                <Button 
                  style={{ backgroundColor: primaryColor }}
                  className="hover:opacity-90"
                  onClick={() => {
                    setShowModal(false);
                    setActiveSection('appointments');
                    setSearchTerm(selectedItem.full_name);
                  }}
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  View Appointments
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    return null;
  };

  return (
    <div 
      className="min-h-screen transition-all duration-300"
      style={{ 
        background: 'var(--main-bg)', 
        fontFamily: `var(--font-body, "${fonts.body}", sans-serif)` 
      }}
    >
      {/* Top Header */}
      <header className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md border-b dark:border-slate-700 sticky top-0 z-50">
        <div className="flex items-center justify-between h-16 px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div className="flex items-center gap-3">
              {clinicBranding?.logo_url ? (
                <img 
                  src={clinicBranding.logo_url} 
                  alt={clinicBranding?.name || 'Clinic'} 
                  className="h-10 w-10 rounded-lg object-cover border shadow-sm"
                />
              ) : (
                <div 
                  className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-sm"
                  style={{ backgroundColor: primaryColor }}
                >
                  {(clinicBranding?.name || 'C').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold" style={{ color: primaryColor }}>
                  {clinicBranding?.name || 'Staff Portal'}
                </h1>
                <p className="text-xs text-slate-500">{roleName || user?.role}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-medium">
                {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            
            <ThemeToggle />
            
            <Button variant="ghost" size="icon" className="relative" onClick={fetchData}>
              <RefreshCw className="h-5 w-5" />
            </Button>
            
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              {stats.unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                  {stats.unreadMessages}
                </span>
              )}
            </Button>
            
            <Separator orientation="vertical" className="h-8" />
            
            <div className="flex items-center gap-2">
              <Avatar className="h-9 w-9" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                <AvatarFallback className="bg-transparent text-white text-sm font-semibold">
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-slate-900 dark:text-white leading-tight">{user?.full_name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{user?.email}</p>
              </div>
            </div>
            
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-16 left-0 z-40 h-[calc(100vh-4rem)] w-64 bg-white dark:bg-slate-800 border-r dark:border-slate-700
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <nav className="p-4 space-y-1">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => { setActiveSection(item.id); setSearchTerm(''); setSidebarOpen(false); }}
                className={`
                  w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all
                  ${activeSection === item.id 
                    ? 'text-white shadow-lg' 
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}
                `}
                style={activeSection === item.id ? { 
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                  boxShadow: `0 10px 25px -5px ${primaryColor}40`
                } : {}}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </div>
                {item.badge && (
                  <Badge className={activeSection === item.id ? 'bg-white/20 text-white' : 'bg-red-500 text-white'}>
                    {item.badge}
                  </Badge>
                )}
              </button>
            ))}
          </nav>
          
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: `linear-gradient(135deg, ${primaryColor}15, ${secondaryColor}10)` }}>
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}20` }}>
                <Zap className="h-5 w-5" style={{ color: primaryColor }} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Quick Tip</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Use keyboard shortcuts!</p>
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/20 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6 min-h-[calc(100vh-4rem)]">
          {activeSection === 'dashboard' && renderDashboard()}
          {activeSection === 'appointments' && renderAppointments()}
          {activeSection === 'members' && renderMembers()}
          {activeSection === 'consultations' && renderConsultations()}
          {activeSection === 'messages' && renderMessages()}
        </main>
      </div>

      {/* Modal */}
      {renderModal()}

      {/* Create Appointment Dialog */}
      <Dialog open={showCreateAppointment} onOpenChange={setShowCreateAppointment}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" style={{ color: primaryColor }} />
              New Appointment
            </DialogTitle>
            <DialogDescription>Create a new appointment for a patient</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium dark:text-white">Patient *</label>
              <Select 
                value={appointmentForm.member_id} 
                onValueChange={(v) => setAppointmentForm(prev => ({ ...prev, member_id: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select patient" />
                </SelectTrigger>
                <SelectContent>
                  {members.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium dark:text-white">Date *</label>
                <Input 
                  type="date"
                  value={appointmentForm.appointment_date}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, appointment_date: e.target.value }))}
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label className="text-sm font-medium dark:text-white">Time *</label>
                <Input 
                  type="time"
                  value={appointmentForm.appointment_time}
                  onChange={(e) => setAppointmentForm(prev => ({ ...prev, appointment_time: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium dark:text-white">Type</label>
              <Select 
                value={appointmentForm.appointment_type} 
                onValueChange={(v) => setAppointmentForm(prev => ({ ...prev, appointment_type: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consultation">Consultation</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="checkup">Checkup</SelectItem>
                  <SelectItem value="procedure">Procedure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium dark:text-white">Duration (minutes)</label>
              <Select 
                value={String(appointmentForm.duration_minutes)} 
                onValueChange={(v) => setAppointmentForm(prev => ({ ...prev, duration_minutes: parseInt(v) }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium dark:text-white">Reason</label>
              <Textarea 
                value={appointmentForm.reason}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Reason for appointment..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateAppointment(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateAppointment}
              disabled={actionLoading.createAppointment}
              style={{ backgroundColor: primaryColor }}
            >
              {actionLoading.createAppointment ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : <><Plus className="h-4 w-4 mr-2" />Create</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={showEditMember} onOpenChange={setShowEditMember}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" style={{ color: primaryColor }} />
              Edit Member
            </DialogTitle>
            <DialogDescription>Update patient information</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium dark:text-white">Full Name *</label>
              <Input 
                value={editMemberForm.full_name || ''}
                onChange={(e) => setEditMemberForm(prev => ({ ...prev, full_name: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium dark:text-white">Email</label>
              <Input 
                type="email"
                value={editMemberForm.email || ''}
                onChange={(e) => setEditMemberForm(prev => ({ ...prev, email: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium dark:text-white">Phone</label>
              <Input 
                value={editMemberForm.phone || ''}
                onChange={(e) => setEditMemberForm(prev => ({ ...prev, phone: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium dark:text-white">Date of Birth</label>
              <Input 
                type="date"
                value={editMemberForm.date_of_birth || ''}
                onChange={(e) => setEditMemberForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium dark:text-white">Address</label>
              <Textarea 
                value={editMemberForm.address || ''}
                onChange={(e) => setEditMemberForm(prev => ({ ...prev, address: e.target.value }))}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditMember(false)}>Cancel</Button>
            <Button 
              onClick={handleEditMember}
              disabled={actionLoading.editMember}
              style={{ backgroundColor: primaryColor }}
            >
              {actionLoading.editMember ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Edit className="h-4 w-4 mr-2" />Save Changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member Detail Dialog - Reusing Admin Component */}
      <MemberDetailDialog
        member={selectedMemberForAction}
        isOpen={memberDetailDialogOpen}
        onClose={() => {
          setMemberDetailDialogOpen(false);
          setSelectedMemberForAction(null);
        }}
        onRefresh={fetchData}
        plans={plans}
      />

      {/* Service Usage Dialog - Reusing Admin Component */}
      <MemberServiceUsageDialog
        member={selectedMemberForAction}
        isOpen={serviceUsageDialogOpen}
        onClose={() => {
          setServiceUsageDialogOpen(false);
          setSelectedMemberForAction(null);
        }}
      />

      {/* Medical Notes Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" style={{ color: primaryColor }} />
              Medical Notes - {selectedMemberForAction?.full_name}
            </DialogTitle>
            <DialogDescription>View and manage patient medical notes</DialogDescription>
          </DialogHeader>
          {selectedMemberForAction && (
            <MedicalNotesPanel 
              memberId={selectedMemberForAction.id} 
              memberName={selectedMemberForAction.full_name}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Assign/Change Plan Dialog - Matching Admin Dashboard */}
      <Dialog open={assignPlanDialogOpen} onOpenChange={(open) => {
        setAssignPlanDialogOpen(open);
        if (!open) {
          setShowPreview(false);
          setPreview(null);
        }
      }}>
        <DialogContent className="max-w-lg dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" style={{ color: primaryColor }} />
              {selectedMemberForAction?.membership_plan_id ? 'Change' : 'Assign'} Membership Plan
            </DialogTitle>
            <DialogDescription>
              {selectedMemberForAction?.full_name} - Current plan: {selectedMemberForAction?.plan_name || 'None'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="dark:text-white">Select Plan *</Label>
              <Select value={selectedPlan} onValueChange={(value) => {
                setSelectedPlan(value);
                setShowPreview(false);
                setPreview(null);
              }}>
                <SelectTrigger className="mt-1" data-testid="plan-select">
                  <SelectValue placeholder="Select a membership plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map(plan => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} - ${plan.price} ({plan.duration_months} months)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="dark:text-white">Start Date (Optional)</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setShowPreview(false);
                  setPreview(null);
                }}
                className="mt-1 dark:bg-slate-700"
                data-testid="start-date-input"
              />
            </div>

            {!showPreview ? (
              <>
                {/* Payment Method Section */}
                <div className="border rounded-lg p-4 bg-gray-50 dark:bg-slate-700 space-y-3">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Payment Details</h4>
                  
                  {/* Gateway Payment Option */}
                  {gatewayStatus?.has_gateway && (
                    <div className="p-3 bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700 rounded-lg mb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-teal-800 dark:text-teal-200">
                            💳 {gatewayStatus.gateway_name} available
                          </p>
                          <p className="text-sm text-teal-600 dark:text-teal-400">
                            Charge directly with credit/debit card
                            {gatewayStatus.test_mode && <span className="ml-1 text-orange-600">(Test mode)</span>}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          className="border-teal-500 text-teal-700 hover:bg-teal-100 dark:text-teal-300 dark:hover:bg-teal-900/50"
                          onClick={() => setPaymentMethod('gateway')}
                        >
                          Use {gatewayStatus.gateway_name}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label className="dark:text-white">Payment Method *</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger className="mt-1" data-testid="payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {gatewayStatus?.has_gateway && (
                          <SelectItem value="gateway">
                            💳 {gatewayStatus.gateway_name} (Online)
                          </SelectItem>
                        )}
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="credit_card">Credit Card (Manual)</SelectItem>
                        <SelectItem value="debit_card">Debit Card (Manual)</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="check">Check</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {paymentMethod === 'gateway' && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        ℹ️ By continuing, the payment will be processed through {gatewayStatus?.gateway_name}. 
                        The customer can pay with their credit or debit card.
                      </p>
                    </div>
                  )}
                  
                  {(paymentMethod === 'check' || paymentMethod === 'bank_transfer' || paymentMethod === 'credit_card' || paymentMethod === 'debit_card') && (
                    <div>
                      <Label className="dark:text-white">Reference Number {paymentMethod === 'check' ? '(Check #)' : ''}</Label>
                      <Input
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        placeholder={paymentMethod === 'check' ? 'Check number' : 'Transaction ID'}
                        className="mt-1 dark:bg-slate-600"
                        data-testid="reference-number-input"
                      />
                    </div>
                  )}
                  
                  {paymentMethod !== 'gateway' && (
                    <div>
                      <Label className="dark:text-white">Payment Notes (Optional)</Label>
                      <Textarea
                        value={paymentNotes}
                        onChange={(e) => setPaymentNotes(e.target.value)}
                        placeholder="Additional payment information..."
                        rows={2}
                        className="mt-1 dark:bg-slate-600"
                        data-testid="payment-notes-input"
                      />
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleGetPreview}
                  className="w-full"
                  style={{ backgroundColor: primaryColor }}
                  disabled={loadingPreview || !selectedPlan}
                  data-testid="preview-btn"
                >
                  {loadingPreview ? 'Calculating...' : 'Continue to Payment Summary'}
                </Button>
              </>
            ) : (
              <div className="space-y-4">
                {/* Payment Method Summary */}
                <div className="border rounded-lg p-3 bg-gray-50 dark:bg-slate-700">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 dark:text-gray-300">Payment Method:</span>
                    <span className="font-medium capitalize dark:text-white">
                      {paymentMethod === 'gateway' ? `${gatewayStatus?.gateway_name} (Online)` : paymentMethod.replace('_', ' ')}
                    </span>
                  </div>
                  {referenceNumber && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">Reference:</span>
                      <span className="font-medium dark:text-white">{referenceNumber}</span>
                    </div>
                  )}
                </div>

                {/* Preview Details */}
                {preview && (
                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-slate-700 space-y-2">
                    <div className="flex justify-between">
                      <span className="dark:text-gray-300">Plan:</span>
                      <span className="font-medium dark:text-white">{preview.plan_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="dark:text-gray-300">Duration:</span>
                      <span className="font-medium dark:text-white">{preview.duration_months} months</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="dark:text-gray-300">Start Date:</span>
                      <span className="font-medium dark:text-white">{new Date(preview.start_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="dark:text-gray-300">End Date:</span>
                      <span className="font-medium dark:text-white">{new Date(preview.end_date).toLocaleDateString()}</span>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between">
                      <span className="dark:text-gray-300">Plan Price:</span>
                      <span className="font-medium dark:text-white">${preview.plan_price?.toFixed(2)}</span>
                    </div>
                    {preview.enrollment_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="dark:text-gray-300">Enrollment Fee:</span>
                        <span className="font-medium dark:text-white">${preview.enrollment_fee?.toFixed(2)}</span>
                      </div>
                    )}
                    {preview.is_new_member && (
                      <p className="text-xs text-orange-600 dark:text-orange-400 italic">* One-time fee for new members</p>
                    )}
                    <Separator className="my-2" />
                    <div className="flex justify-between text-lg font-bold">
                      <span className="dark:text-white">Total Due:</span>
                      <span style={{ color: primaryColor }}>${preview.total_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setShowPreview(false);
                      setPreview(null);
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleAssignPlan}
                    disabled={assignLoading}
                    className={`flex-1 ${paymentMethod === 'gateway' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    style={paymentMethod !== 'gateway' ? { backgroundColor: primaryColor } : {}}
                    data-testid="confirm-assign-btn"
                  >
                    {assignLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                    ) : paymentMethod === 'gateway' ? (
                      <><CreditCard className="h-4 w-4 mr-2" />Pay with {gatewayStatus?.gateway_name}</>
                    ) : (
                      <><Award className="h-4 w-4 mr-2" />Confirm & Record Payment</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={paymentHistoryDialogOpen} onOpenChange={setPaymentHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" style={{ color: primaryColor }} />
              Payment History - {selectedMemberForAction?.full_name}
            </DialogTitle>
            <DialogDescription>View all payments made by this member</DialogDescription>
          </DialogHeader>
          
          {loadingPayments ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : memberPayments.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No payment records found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {memberPayments.map(payment => (
                <Card key={payment.id} className="dark:bg-slate-700">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium dark:text-white">{payment.description}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {payment.payment_method?.replace('_', ' ')} • {payment.reference_number || 'No ref'}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(payment.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold" style={{ color: primaryColor }}>
                          ${payment.amount?.toFixed(2)}
                        </p>
                        <Badge variant={payment.payment_status === 'completed' ? 'default' : 'secondary'}>
                          {payment.payment_status}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add New Member Dialog (Walk-in Registration) */}
      <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" style={{ color: primaryColor }} />
              Register Walk-in Patient
            </DialogTitle>
            <DialogDescription>
              Create a new member account. An email will be sent with login credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="dark:text-white">Full Name *</Label>
                <Input
                  autoComplete="name"
                  value={newMemberData.full_name}
                  onChange={(e) => setNewMemberData({...newMemberData, full_name: e.target.value})}
                  placeholder="John Doe"
                  className="mt-1 dark:bg-slate-700"
                />
              </div>
              <div className="col-span-2">
                <Label className="dark:text-white">Email *</Label>
                <Input
                  type="email"
                  autoComplete="email"
                  value={newMemberData.email}
                  onChange={(e) => setNewMemberData({...newMemberData, email: e.target.value})}
                  placeholder="john@example.com"
                  className="mt-1 dark:bg-slate-700"
                />
              </div>
              <div>
                <Label className="dark:text-white">Phone</Label>
                <Input
                  type="tel"
                  autoComplete="tel"
                  value={newMemberData.phone}
                  onChange={(e) => setNewMemberData({...newMemberData, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                  className="mt-1 dark:bg-slate-700"
                />
              </div>
              <div>
                <Label className="dark:text-white">Date of Birth</Label>
                <Input
                  type="date"
                  autoComplete="bday"
                  value={newMemberData.date_of_birth}
                  onChange={(e) => setNewMemberData({...newMemberData, date_of_birth: e.target.value})}
                  className="mt-1 dark:bg-slate-700"
                />
              </div>
              <div className="col-span-2">
                <Label className="dark:text-white">Address</Label>
                <Textarea
                  value={newMemberData.address}
                  onChange={(e) => setNewMemberData({...newMemberData, address: e.target.value})}
                  placeholder="Street address, city, state, zip"
                  className="mt-1 dark:bg-slate-700"
                  rows={2}
                />
              </div>
              <div>
                <Label className="dark:text-white">Emergency Contact</Label>
                <Input
                  autoComplete="off"
                  value={newMemberData.emergency_contact}
                  onChange={(e) => setNewMemberData({...newMemberData, emergency_contact: e.target.value})}
                  placeholder="Contact name"
                  className="mt-1 dark:bg-slate-700"
                />
              </div>
              <div>
                <Label className="dark:text-white">Emergency Phone</Label>
                <Input
                  type="tel"
                  autoComplete="off"
                  value={newMemberData.emergency_phone}
                  onChange={(e) => setNewMemberData({...newMemberData, emergency_phone: e.target.value})}
                  placeholder="(555) 987-6543"
                  className="mt-1 dark:bg-slate-700"
                />
              </div>
              <div className="col-span-2">
                <Label className="dark:text-white">Membership Plan (Optional)</Label>
                <Select 
                  value={newMemberData.plan_id} 
                  onValueChange={(value) => setNewMemberData({...newMemberData, plan_id: value === 'none' ? '' : value})}
                >
                  <SelectTrigger className="mt-1 dark:bg-slate-700">
                    <SelectValue placeholder="Select a plan (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No plan - Add later</SelectItem>
                    {plans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - ${plan.price}/{plan.duration_months} month(s)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send_welcome_email_staff"
                  checked={newMemberData.send_welcome_email}
                  onChange={(e) => setNewMemberData({...newMemberData, send_welcome_email: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="send_welcome_email_staff" className="cursor-pointer dark:text-white">
                  Send welcome email with login credentials
                </Label>
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setAddMemberDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateMember}
                disabled={creatingMember || !newMemberData.full_name || !newMemberData.email}
                className="flex-1"
                style={{ backgroundColor: primaryColor }}
              >
                {creatingMember ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Member
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Dialog */}
      <Dialog open={qrScannerOpen} onOpenChange={(open) => {
        setQrScannerOpen(open);
        if (!open) {
          setScannedMember(null);
          setScanError(null);
          setQrScanning(true);
        }
      }}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-purple-600" />
              QR Check-in Scanner
            </DialogTitle>
            <DialogDescription>
              {scannedMember 
                ? 'Member found! Confirm check-in below.' 
                : 'Point the camera at the member\'s QR code'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {!scannedMember ? (
              // Scanner mode
              <>
                {qrScanning && (
                  <div className="relative rounded-lg overflow-hidden bg-black" style={{ minHeight: '280px' }}>
                    <Scanner
                      onScan={handleQrScan}
                      onError={(error) => {
                        console.error('Scanner error:', error);
                        setScanError('Camera access error. Please check permissions.');
                      }}
                      constraints={{
                        facingMode: 'environment'
                      }}
                      styles={{
                        container: { width: '100%', height: '280px' },
                        video: { width: '100%', height: '100%', objectFit: 'cover' }
                      }}
                    />
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute inset-0 border-2 border-purple-500 rounded-lg m-8 opacity-50"></div>
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <div className="w-48 h-48 border-2 border-purple-400 rounded-lg animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                )}
                
                {scanError && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                      <AlertCircle className="h-5 w-5" />
                      <p className="text-sm font-medium">{scanError}</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={resetQrScanner}
                    >
                      Try Again
                    </Button>
                  </div>
                )}

                <div className="text-center text-sm text-slate-500 dark:text-slate-400">
                  <Camera className="h-5 w-5 mx-auto mb-1" />
                  Scanning for QR code...
                </div>
              </>
            ) : (
              // Member found - confirmation mode
              <>
                <Card className={`${scannedMember.membership_status === 'active' ? 'border-emerald-200 dark:border-emerald-700' : 'border-red-200 dark:border-red-700'} dark:bg-slate-700`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Avatar className={`h-16 w-16 ${scannedMember.membership_status === 'active' ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'}`}>
                        <AvatarFallback className="bg-transparent text-white text-xl font-bold">
                          {getInitials(scannedMember.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg dark:text-white">{scannedMember.full_name}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Patient ID: {scannedMember.patient_id}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={scannedMember.membership_status === 'active' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-red-100 text-red-700'}>
                            {scannedMember.membership_status}
                          </Badge>
                          {scannedMember.plan_name && (
                            <Badge variant="outline">{scannedMember.plan_name}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {scannedMember.membership_status !== 'active' && (
                      <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          This member's membership is not active. Check-in not allowed.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={resetQrScanner}
                    className="flex-1"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Scan Another
                  </Button>
                  <Button 
                    onClick={handleQrCheckIn}
                    disabled={checkInLoading || scannedMember.membership_status !== 'active'}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {checkInLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Checking in...
                      </>
                    ) : (
                      <>
                        <UserCheck className="h-4 w-4 mr-2" />
                        Confirm Check-in
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fast Check-in Dialog */}
      <Dialog open={checkInDialogOpen} onOpenChange={setCheckInDialogOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-emerald-600" />
              Fast Check-in
            </DialogTitle>
            <DialogDescription>
              {checkInMember 
                ? `Check in ${checkInMember.full_name}` 
                : 'Select a member to check in'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {!checkInMember ? (
              // Member selection mode
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search active members..."
                    className="pl-10 dark:bg-slate-700"
                    onChange={(e) => {
                      const term = e.target.value.toLowerCase();
                      // Filter will be applied in the list below
                    }}
                  />
                </div>
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {members
                      .filter(m => m.membership_status === 'active')
                      .map(member => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                          onClick={() => setCheckInMember(member)}
                        >
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-emerald-600">
                              <AvatarFallback className="bg-transparent text-white text-sm font-bold">
                                {getInitials(member.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium dark:text-white">{member.full_name}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">ID: {member.patient_id}</p>
                            </div>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                        </div>
                      ))}
                    {members.filter(m => m.membership_status === 'active').length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        No active members found
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </>
            ) : (
              // Check-in confirmation mode
              <>
                <Card className="dark:bg-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-16 w-16 bg-gradient-to-br from-emerald-500 to-emerald-600">
                        <AvatarFallback className="bg-transparent text-white text-xl font-bold">
                          {getInitials(checkInMember.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-semibold text-lg dark:text-white">{checkInMember.full_name}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Patient ID: {checkInMember.patient_id}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{checkInMember.email}</p>
                        <Badge className="mt-1 bg-emerald-100 text-emerald-700">{checkInMember.plan_name || 'Active Member'}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div>
                  <Label className="dark:text-white">Visit Reason (Optional)</Label>
                  <Textarea
                    value={checkInReason}
                    onChange={(e) => setCheckInReason(e.target.value)}
                    placeholder="e.g., Monthly check-up, Follow-up visit..."
                    className="mt-1 dark:bg-slate-700"
                    rows={2}
                  />
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setCheckInMember(null)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleFastCheckIn}
                    disabled={checkInLoading}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {checkInLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Checking in...
                      </>
                    ) : (
                      <>
                        <UserCheck className="h-4 w-4 mr-2" />
                        Confirm Check-in
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Recent Check-ins */}
          {recentCheckIns.length > 0 && !checkInMember && (
            <div className="pt-4 border-t dark:border-slate-700">
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Recent Check-ins</p>
              <div className="space-y-2">
                {recentCheckIns.map((checkin, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm p-2 bg-slate-50 dark:bg-slate-700 rounded">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      <span className="dark:text-white">{checkin.member.full_name}</span>
                    </div>
                    <span className="text-slate-400 text-xs">
                      {checkin.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffDashboard;
